import { DEFAULT_BOOKING_INFORMATION_FIELDS, DEFAULT_COMPENSATION_FIELDS, DEFAULT_FLIGHT_INFORMATION_FIELDS, DEFAULT_PASSENGER_INFORMATION_FIELDS } from '../constants/default';
import { Dispatch, SetStateAction } from 'react';
import { FIELD_NAMES } from '../constants/common';
import { getUtcDate } from './date';
import { TFunction } from 'i18next';
import CryptoES from 'crypto-es';
import { error } from './apiConfig';

export const getFields = (setResendErroredVoucherId: Dispatch<SetStateAction<string>>, t: TFunction<'translation', undefined, 'translation'>) => {
    const encryptedString = sessionStorage.getItem('OBXData');
    const nonIFSEditFieldsString = sessionStorage.getItem('HistoryEditFields');
    const recentFormValuesString = sessionStorage.getItem('RecentFormValues');
    const newFormSamePassengerString = sessionStorage.getItem('NewFormSamePassenger');

    if (encryptedString) {
        return handleOnDeepLink(encryptedString);
    }
    else if (nonIFSEditFieldsString) {
        const nonIFSEditFieldsObj = JSON.parse(nonIFSEditFieldsString);
        return handleOnNonIFSErrorVoucherEdit(nonIFSEditFieldsObj, setResendErroredVoucherId, t);
    }
    else if (newFormSamePassengerString && recentFormValuesString) {
        const recentFormValuesObj = JSON.parse(recentFormValuesString);
        return handleOnNewFormSamePassenger(recentFormValuesObj);
    }
    else {
        return {
            bookingInformation: JSON.parse(JSON.stringify(DEFAULT_BOOKING_INFORMATION_FIELDS)),
            compensationInformation: JSON.parse(JSON.stringify(DEFAULT_COMPENSATION_FIELDS)),
            flightInformation: JSON.parse(JSON.stringify(DEFAULT_FLIGHT_INFORMATION_FIELDS)),
            passengerInformation: JSON.parse(JSON.stringify(DEFAULT_PASSENGER_INFORMATION_FIELDS))
        }
    }
};

const handleOnDeepLink = (encryptedString: string) => {
    const secretKey = 'AC-OBX-SECRET'; 
    const decoded = decodeURIComponent(encryptedString);
    const bytes = CryptoES.AES.decrypt(decoded, secretKey);
    const originalData = bytes.toString(CryptoES.enc.Utf8);
    const parsedObject = JSON.parse(originalData);

    const flightInformationFields = JSON.parse(JSON.stringify(DEFAULT_FLIGHT_INFORMATION_FIELDS));
    flightInformationFields[0].value = parsedObject?.flightNumber || '';
    flightInformationFields[1].value = parsedObject?.date ? getUtcDate(parsedObject.date) : '';

    const bookingInformationFields = JSON.parse(JSON.stringify(DEFAULT_BOOKING_INFORMATION_FIELDS));
    bookingInformationFields[0].value = parsedObject?.pnr || '';

    const passengerInformationFields = JSON.parse(JSON.stringify(DEFAULT_PASSENGER_INFORMATION_FIELDS));
    passengerInformationFields[0].value = parsedObject?.givenName || '';
    passengerInformationFields[1].value = parsedObject?.lastName || '';
    passengerInformationFields[2].value = parsedObject?.email || '';
    passengerInformationFields[3].value = parsedObject?.email || '';

    return {
        bookingInformation: bookingInformationFields,
        compensationInformation: JSON.parse(JSON.stringify(DEFAULT_COMPENSATION_FIELDS)),
        flightInformation: flightInformationFields,
        passengerInformation: passengerInformationFields
    }
};

const handleOnNewFormSamePassenger = (state) => {
    const flightInformationFields = JSON.parse(JSON.stringify(DEFAULT_FLIGHT_INFORMATION_FIELDS));
    flightInformationFields[0].value = state[FIELD_NAMES.flightNumber].replace('AC', '');
    flightInformationFields[1].value = state[FIELD_NAMES.date] ? getUtcDate(state[FIELD_NAMES.date]) : '';

    const bookingInformationFields = JSON.parse(JSON.stringify(DEFAULT_BOOKING_INFORMATION_FIELDS));
    if (state[FIELD_NAMES.bookingReference]) {
        bookingInformationFields[0].value  = state[FIELD_NAMES.bookingReference];
        bookingInformationFields[1].disabled  = true;
    }
    else if (state[FIELD_NAMES.ticketNumber]) {
        bookingInformationFields[1].value = state[FIELD_NAMES.ticketNumber];
        bookingInformationFields[0].disabled = true;
    }

    const passengerInformationFields = JSON.parse(JSON.stringify(DEFAULT_PASSENGER_INFORMATION_FIELDS));
    passengerInformationFields[0].value = state[FIELD_NAMES.firstName];
    passengerInformationFields[1].value = state[FIELD_NAMES.lastName];
    passengerInformationFields[2].value = state[FIELD_NAMES.email];
    passengerInformationFields[3].value = state[FIELD_NAMES.confirmEmail];

    return {
        bookingInformation: bookingInformationFields,
        compensationInformation: JSON.parse(JSON.stringify(DEFAULT_COMPENSATION_FIELDS)),
        flightInformation: flightInformationFields,
        passengerInformation: passengerInformationFields
    }
};

const handleOnNonIFSErrorVoucherEdit = (state, setResendErroredVoucherId: Dispatch<SetStateAction<string>>, t: TFunction<'translation', undefined, 'translation'>) => {
    const flightInformationFields = JSON.parse(JSON.stringify(DEFAULT_FLIGHT_INFORMATION_FIELDS));
    flightInformationFields[0].value = state.flight?.replace('AC', '') || '';
    flightInformationFields[1].value = state.date ? getUtcDate(state.date) : '';

    const bookingInformationFields = JSON.parse(JSON.stringify(DEFAULT_BOOKING_INFORMATION_FIELDS));
    if (state.bookingReference?.length === 6) {
        bookingInformationFields[0].value = state.bookingReference;
        bookingInformationFields[1].disabled = true;
    }
    else if (state.bookingReference?.length === 13) {
        bookingInformationFields[1].value = state.bookingReference;
        bookingInformationFields[0].disabled = true;
    }

    const passengerInformationFields = JSON.parse(JSON.stringify(DEFAULT_PASSENGER_INFORMATION_FIELDS));
    if (state.customer) {
        const nameParts = state.customer.split(' ');
        passengerInformationFields[0].value = nameParts[0] || '';
        passengerInformationFields[1].value = nameParts.slice(1).join(' ') || '';
    }
    passengerInformationFields[2].value = state.email || '';
    passengerInformationFields[3].value = state.email || '';

    const compensationInformationFields = JSON.parse(JSON.stringify(DEFAULT_COMPENSATION_FIELDS));
    
    // Set reason ID and display value
    compensationInformationFields[0].value = state.reasonId || '';
    compensationInformationFields[0].displayValue = state.reason || '';
    
    // Set category ID and display value
    compensationInformationFields[1].value = state.categoryId || '';
    compensationInformationFields[1].displayValue = state.category || '';
    
    // Set cabin ID and display value
    // For the cabin field, we need to set both the ID and display value
    compensationInformationFields[2].value = state.cabinId || '';
    compensationInformationFields[2].displayValue = state.cabin || '';
    // Also set a special property to store the display text for the dropdown
    compensationInformationFields[2]._displayText = state.cabin || '';

    // Set other fields
    compensationInformationFields[3].value = String(state.qty || '1');
    compensationInformationFields[4].value = state.comments || '';
    compensationInformationFields[5].value = state.type || '';

    // Disable all fields
    compensationInformationFields.forEach(field => {
        field.disabled = true;
    });

    if (state.type === 'Wi-Fi' && state.subType) {
        compensationInformationFields[6].value = `${t(`wifiRegion.${state.subType.toLowerCase()}`)}`;
        compensationInformationFields[6].disabled = true;
    }

    setResendErroredVoucherId(state.id);

    return {
        bookingInformation: bookingInformationFields,
        compensationInformation: compensationInformationFields,
        flightInformation: flightInformationFields,
        passengerInformation: passengerInformationFields
    }
};

export async function processOfflineRequests({
  isDeviceOffline,
  acquireTokenSilently,
  offlineModuleMutation,
  setErrorCode
}: {
  isDeviceOffline: boolean;
  acquireTokenSilently: () => Promise<string>;
  offlineModuleMutation: (args: { variables: { payload: any } }) => Promise<any>;
  setErrorCode: (err: any) => void;
}) {
  if (isDeviceOffline) return;
  try {
    const refreshedToken = await acquireTokenSilently();
    if (!refreshedToken) return;
    // Defensive check for offlineNtpRequests
    let offlineRequestsRaw = localStorage.getItem('offlineNtpRequests');
    let offlineRequests: any[] = [];
    try {
      const parsed = JSON.parse(offlineRequestsRaw ?? '[]');
      offlineRequests = Array.isArray(parsed) ? parsed : [];
    } catch {
      offlineRequests = [];
      localStorage.removeItem('offlineNtpRequests'); // Clean up corrupted data
    }
    if (!offlineRequests.length) return;
    let updatedRequests = [...offlineRequests];
    for (const request of offlineRequests) {
      try {
        const { data } = await offlineModuleMutation({ variables: { payload: request } });
        let statusCode = data?.stkOfflineModule?.statusCode ?? null;
        let code = data?.stkOfflineModule?.code ?? null;
        let message = data?.stkOfflineModule?.message ?? '';
        let responseData = data?.stkOfflineModule?.data ?? {};
        if (typeof responseData === 'string') {
          try {
            const parsed = JSON.parse(responseData);
            statusCode = parsed.statusCode ?? statusCode;
            code = parsed.code ?? code;
            message = parsed.message ?? message;
            responseData = parsed.data ?? responseData;
          } catch (e) {}
        }
        if (statusCode === 200 || statusCode === 409) {
          updatedRequests = updatedRequests.filter(r => r.requestId !== request.requestId);
          localStorage.setItem('offlineNtpRequests', JSON.stringify(updatedRequests));
        } else {
          console.error('Failed to process offline request:', request.requestId, { statusCode, code, message, responseData });
        }
      } catch (e) {
        const errorMessage = (typeof e === 'object' && e !== null && 'message' in e) ? (e as any).message : e;
        console.error('Error processing offline request:', errorMessage);
      }
    }
  } catch (e) {
    const errorMessage = (typeof e === 'object' && e !== null && 'message' in e) ? (e as any).message : e;
    console.error('Error in processOfflineRequests:', errorMessage);
    setErrorCode(error.DEFAULT);
  }
}

export function cleanVoucherSubTypeCode(subType: string): string {
    if (typeof subType === 'string' && subType.split('_').length > 2) {
        const parts = subType.split('_');
        return parts.slice(parts.length - 2).join('_');
    }
    return subType;
}
